'use client';

import { DatePickerInput as MantineDatePickerInput } from '@mantine/dates';
import type { DatePickerInputProps as MantineDatePickerInputProps } from '@mantine/dates';
import dayjs from 'dayjs';

import { useGetDateValueFormat } from '@highlight-cp/hooks';

import type { LockedProps } from '../types';

export type DateRange = [Date | null, Date | null];

// Like `DateInput`: Mantine emits date strings, so this keeps the Date-based public API, and
// `valueFormat` is the user's display-format preference, so it is locked. A range field is often a
// compact toolbar control with no room for a visible label, so it takes an `aria-label` instead of
// a `label`, and one of the two is always there.
type DateRangeInputProps = Omit<
  MantineDatePickerInputProps<'range'>,
  LockedProps | 'type' | 'value' | 'defaultValue' | 'onChange' | 'valueFormat' | 'label'
> & {
  'aria-label': string;
  value: DateRange;
  onChange: (value: DateRange) => void;
};

const toDate = (value: string | Date | null) => (value ? dayjs(value).toDate() : null);

/**
 * ### DateRangeInput
 *
 * @description
 * One field that picks a first and last day from a single calendar. Emits a
 * `[from, to]` pair of Dates, with `null` for an end not picked yet: the first
 * click gives `[from, null]`, so a controlled field must hold that pair to let
 * the second click finish the range.
 *
 * @example
 * <DateRangeInput aria-label="Usage range" value={range} onChange={setRange} maxDate={new Date()} />
 *
 * @param props - The range, its change handler, and Mantine's date picker props
 * @returns The field
 */
export const DateRangeInput = ({ value, onChange, ...rest }: DateRangeInputProps) => {
  const valueFormat = useGetDateValueFormat();
  const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;

  return (
    <MantineDatePickerInput
      // Defaults, so they sit before the spread and stay overridable per call site.
      highlightToday
      locale={locale}
      {...rest}
      type="range"
      valueFormat={valueFormat}
      value={value}
      onChange={([from, to]) => onChange([toDate(from), toDate(to)])}
    />
  );
};
